"use client";

/**
 * One film on the 16:9 stage (sized by its own width, cqw): the backdrop, the
 * current beat's headline, pills and drawn tool, the film's name and the
 * progress line. The clock lives outside (`useBeatClock`), so the same stage
 * runs unattended on /screens and with controls on /why-bb/story.
 */

import { Fragment } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { BBSignature } from "@/components/home/bb-signature";
import { cn } from "@/lib/utils";
import type { Film, LinesFor } from "./films";
import { useFilmAudio } from "./film-art";
import { Mock } from "./mocks";

export const FADE_MS = 700;

/** Oswald headlines are upper case, which turns π into Π; keep it π. */
export function piSafe(text: string) {
  if (!text.includes("π")) return text;
  return text.split("π").map((part, i, all) => (
    <Fragment key={i}>{part}{i < all.length - 1 && <span className="normal-case">π</span>}</Fragment>
  ));
}

export function filmNamespace(film: Film) {
  return film.ns ?? `screens.${film.id}`;
}

/** BB's release covers in three drifting rows behind the words (Gordon 2026-11-03: fill the whole stage). */
export function CoverWall({ covers }: { covers: string[] }) {
  if (!covers.length) return null;
  return (
    // Three rows of square covers fill the whole stage, top to bottom.
    <div aria-hidden className="bb-intro-pop absolute inset-0 flex flex-col gap-[0.8cqw] py-[0.8cqw]">
      {[0, 1, 2].map(r => (
        <div key={r} className="min-h-0 flex-1 overflow-hidden">
          <div className={cn("flex h-full w-max gap-[0.8cqw] bb-intro-drift", r === 1 && "bb-intro-drift-reverse")}>
            {[...covers.filter((_, i) => i % 3 === r), ...covers.filter((_, i) => i % 3 === r)].map((src, i) => (
              <div key={i} className="relative aspect-square h-full shrink-0 overflow-hidden rounded-[0.6cqw]">
                <Image src={src} alt="" fill sizes="20vw" className="object-cover" />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#171512]/95 via-[#171512]/80 to-[#171512]/60" />
    </div>
  );
}

export function FilmStage({ film, beat, progress, round, covers, people, linesFor }: {
  film: Film;
  beat: number;
  progress: number;
  round: number;
  covers: string[];
  people: string[];
  linesFor: (k: LinesFor) => string[];
}) {
  const t = useTranslations(filmNamespace(film));
  // Lines every role film ends on ("Free to join" …).
  const tShared = useTranslations("roleFilms.shared");
  const tx = (k: string) => (t.has(k) ? t(k) : tShared(k));
  const audio = useFilmAudio();
  // A different real track per film and beat.
  const seed = [...film.id].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0) + beat;

  const b = film.beats[beat];
  const total = film.beats.reduce((s, x) => s + x.ms, 0);
  const before = film.beats.slice(0, beat).reduce((s, x) => s + x.ms, 0);
  const leaving = progress * b.ms > b.ms - FADE_MS;
  const headline = tx(b.h);
  const lines = b.lines ? linesFor(b.lines) : undefined;
  // BB's artwork behind every beat unless it shows people (in colour).
  const bg = b.bg ?? "covers";

  return (
    <div className="relative h-full w-full">
      {/* Backdrop */}
      {bg === "covers" && <CoverWall covers={covers} />}
      {bg === "people" && people.length > 0 && (
        <div aria-hidden key={`bg-${beat}-${round}`} className="absolute inset-0 grid grid-cols-8 content-center gap-[0.6cqw] overflow-hidden p-[0.6cqw]">
          {Array.from({ length: 40 }, (_, i) => people[i % people.length]).map((src, i) => (
            <div key={i} className="sm-in relative aspect-square overflow-hidden rounded-[0.6cqw]" style={{ animationDelay: `${(i % 8) * 110 + Math.floor(i / 8) * 160}ms` }}>
              <Image src={src} alt="" fill sizes="13vw" className="object-cover" />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-[#171512] via-[#171512]/85 to-[#171512]/50" />
        </div>
      )}

      {/* The beat: words left, the drawn tool right — the tool shrinks when
          there are many pills, so nothing overlaps or runs into the footer. */}
      {(() => {
        const pillText = (b.pills ?? []).map(k => tx(k));
        const longest = Math.max(0, ...pillText.map(p => p.length));
        const many = pillText.length > 3 || longest > 30;
        const size = pillText.length
          ? (headline.length > 42 ? "text-[3.8cqw]" : headline.length > 26 ? "text-[4.4cqw]" : "text-[5.2cqw]")
          : (headline.length > 42 ? "text-[4.6cqw]" : headline.length > 26 ? "text-[5.6cqw]" : "text-[6.6cqw]");
        return (
          <div key={`${beat}-${round}`} aria-live="polite"
            className={cn("absolute inset-0 grid items-center gap-[3cqw] px-[6cqw] pb-[8.5cqw] pt-[7.5cqw] transition-opacity duration-700",
              b.mock ? "grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-1", leaving ? "opacity-0" : "opacity-100")}>
            <div className="min-w-0 space-y-[1.8cqw]">
              <p className={cn("bb-intro-rise bb-impact leading-[1.03]", size)} style={{ animationDelay: "150ms" }}>
                {piSafe(headline)}
              </p>
              {pillText.length > 0 && (
                <ul className="flex flex-wrap gap-[0.8cqw]">
                  {pillText.map((text, i) => (
                    <li key={i} className={cn("bb-intro-pop rounded-full bg-[#f5f1ea] font-semibold leading-tight text-[#171512]",
                      many ? "px-[1.3cqw] py-[0.5cqw] text-[1.9cqw]" : "px-[1.6cqw] py-[0.6cqw] text-[2.3cqw]")}
                      style={{ animationDelay: `${700 + i * 380}ms` }}>
                      {piSafe(text)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {b.mock && (
              <div className="justify-self-end" style={{ zoom: pillText.length ? (many ? 0.72 : 0.82) : 1 }}>
                <Mock id={b.mock} covers={covers} people={people} lines={lines} audio={audio} seed={seed} />
              </div>
            )}
          </div>
        );
      })()}

      {/* Film name, top left */}
      <p className="absolute left-[6cqw] top-[3.4cqw] text-[1.5cqw] font-bold uppercase tracking-[0.3em] opacity-70">{piSafe(tx("name"))}</p>

      {/* Footer: progress, address, mark */}
      <div className="absolute inset-x-[6cqw] bottom-[2.6cqw] flex items-center gap-[2cqw]">
        <span className="text-[1.4cqw] font-semibold tracking-wide opacity-80">breakinbread.com</span>
        <span className="h-[0.25cqw] flex-1 overflow-hidden rounded-full bg-[#f5f1ea]/15">
          <span className="block h-full origin-left bg-[#f5f1ea]" style={{ transform: `scaleX(${(before + progress * b.ms) / total})` }} />
        </span>
        <BBSignature className="w-[3.4cqw] bg-[#f5f1ea]" />
      </div>
    </div>
  );
}
